import React from 'react';
import { AlertTriangle, CheckCircle, Calendar, FileText, TrendingUp, AlertCircle } from 'lucide-react';
import { Button } from './Button';

export const SummaryValidation = () => {
    const summaryData = {
        outwardSupply: 125000,
        inwardSupply: 75000,
        taxPayable: 15000,
        itcAvailable: 12000,
        netTaxPayable: 3000,
        alerts: [
            { type: 'error', message: 'Missing GSTIN for 2 invoices', count: 2 },
            { type: 'warning', message: 'Pending e-way bills', count: 5 },
            { type: 'info', message: 'Due date approaching', count: 1 }
        ],
        complianceStatus: {
            gstr1: { status: 'filed', dueDate: '2024-01-20', filed: true },
            gstr3b: { status: 'pending', dueDate: '2024-01-20', filed: false },
            gstr2a: { status: 'auto-populated', dueDate: '2024-01-20', filed: true }
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'filed': return 'bg-green-100 text-green-800';
            case 'pending': return 'bg-red-100 text-red-800';
            case 'auto-populated': return 'bg-blue-100 text-blue-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getAlertIcon = (type) => {
        switch (type) {
            case 'error': return <AlertTriangle className="h-4 w-4 text-red-500" />;
            case 'warning': return <AlertCircle className="h-4 w-4 text-orange-500" />;
            case 'info': return <CheckCircle className="h-4 w-4 text-blue-500" />;
            default: return <AlertTriangle className="h-4 w-4 text-gray-500" />;
        }
    };


    return (
        <div className="space-y-6">
            {/* Tax Summary Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Tax Summary</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div className="space-y-3">
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Outward Supply</span>
                            <span className="font-semibold">₹{summaryData.outwardSupply.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Inward Supply</span>
                            <span className="font-semibold">₹{summaryData.inwardSupply.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Tax Payable</span>
                            <span className="font-semibold text-red-600">₹{summaryData.taxPayable.toLocaleString()}</span>
                        </div>
                    </div>

                    <div className="space-y-3">
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">ITC Available</span>
                            <span className="font-semibold text-green-600">₹{summaryData.itcAvailable.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-center border-t pt-3">
                            <span className="text-sm font-medium text-gray-900">Net Tax Payable</span>
                            <span className="font-bold text-lg text-blue-600">₹{summaryData.netTaxPayable.toLocaleString()}</span>
                        </div>
                    </div>
                </div>

                <div className="flex justify-center">
                    <Button label="Validate & Generate Returns" />
                </div>


            </div>

            {/* Compliance Status */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Filing Status</h3>

                <div className="space-y-3">
                    {Object.entries(summaryData.complianceStatus).map(([key, value]) => (
                        <div key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center space-x-3">
                                <FileText className="h-5 w-5 text-gray-400" />
                                <div>
                                    <span className="font-medium text-gray-900 uppercase">{key}</span>
                                    <div className="text-sm text-gray-500">Due: {value.dueDate}</div>
                                </div>
                            </div>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(value.status)}`}>
                                {value.status.replace('-', ' ')}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Alerts */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Alerts & Notifications</h3>

                <div className="space-y-3">
                    {summaryData.alerts.map((alert, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center space-x-3">
                                {getAlertIcon(alert.type)}
                                <span className="text-sm text-gray-700">{alert.message}</span>
                            </div>
                            <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium">
                                {alert.count}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <button className="flex items-center space-x-2 p-3 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors">
                        <Calendar className="h-4 w-4" />
                        <span>Schedule Filing</span>
                    </button>
                    <button className="flex items-center space-x-2 p-3 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors">
                        <FileText className="h-4 w-4" />
                        <span>Generate Reports</span>
                    </button>
                    <button className="flex items-center space-x-2 p-3 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors">
                        <TrendingUp className="h-4 w-4" />
                        <span>View Analytics</span>
                    </button>
                    <button className="flex items-center space-x-2 p-3 bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 transition-colors">
                        <AlertTriangle className="h-4 w-4" />
                        <span>Resolve Alerts</span>
                    </button>
                </div>
            </div>
        </div>
    );
};